import { Alert, Button, type AlertTone } from '@nexvia-logistics/ui';
import type { ApprovalStatus, StorageNextStep } from './storage-api';
import { DOCUMENTS_SECTION_ID } from './storage-documents.constants';

export interface ApprovalStatusBannerProps {
  status: ApprovalStatus;
  nextStep: StorageNextStep | null;
  /**
   * Only used for the `verified` state's "Alta aprobada el [fecha]" headline — every other status
   * ignores it. `storage-api.ts`'s `StorageProfile.reviewedAt` is itself optional (present on
   * `GET /storage/me`, absent on a `PATCH` response), and may be `null` even when present (see that
   * field's own docblock in `src/contracts/storage-profile.ts` for the real-data caveat). Either
   * case renders the headline without a date rather than fabricating one.
   */
  reviewedAt?: string | null;
  /**
   * NEX-1164 — the reason of the latest reject OR suspend (`GET /storage/me`'s `rejectionReason`,
   * one column for both, cleared by the API on approval or resubmission). Shown only for
   * `rejected`/`suspended`.
   */
  rejectionReason?: string | null;
  /** NEX-1164 — when the third document arrived (`GET /storage/me`'s `submittedAt`); `pending_review` only. */
  submittedAt?: string | null;
}

interface StatusCopy {
  label: string;
  tone: AlertTone;
  fallbackMessage: string;
  /** Shows the stored reason (`rejected`/`suspended`). */
  showsReason?: boolean;
  /** NEX-1164 — the upload call to action, a link to `DocumentsCard` (`#documentacion`) on this
   *  same page: only where uploading documents is what moves the account forward. */
  uploadCta?: string;
}

/** Every `ApprovalStatus` except `verified`, which now has its own dedicated render branch below
 *  (real date headline, not a label/message pair from this table). */
type NonVerifiedStatus = Exclude<ApprovalStatus, 'verified'>;

/**
 * `nextStep` is null for `pending_review`/`verified` (`get-storage-profile.handler.ts`'s own
 * comment: "nothing actionable while Nexvia reviews it, and nothing left to do once it is
 * verified") — `fallbackMessage` is this screen's own copy for exactly those two states; every
 * other state always has a real `nextStep.message` from the backend, but the fallback is kept here
 * too so a null `nextStep` never renders a blank banner.
 *
 * NEX-1151: `tone` maps onto the real `Alert`'s own narrower tone set (`AlertTone` = `info` |
 * `positive` | `caution` | `critical`, confirmed in `Alert.d.ts` — no `neutral`/`active`).
 * `rejected`/`suspended` are both `critical` (the account cannot operate); `incomplete` is
 * `caution` (actionable, not urgent); `pending_review` is `info` (a neutral waiting state).
 */
/*
 * NEX-1164 — who moves the account between these states lives in `nexvia-api-web`'s
 * `domain/account-approval.ts` (summary in `src/pages/empresa/AGENTS.md`): the depósito moves
 * `incomplete`/`rejected` → `pending_review` by confirming its third required document; Nexvia
 * approves, rejects or suspends from the backoffice. After the registration form the company data
 * is always complete, so `incomplete` means "documents missing" — hence its label (it used to read
 * "Datos incompletos"). `suspended` gets no upload CTA: re-uploading does not lift a suspension,
 * only Nexvia's approve does.
 */
const STATUS_COPY: Record<NonVerifiedStatus, StatusCopy> = {
  incomplete: {
    label: 'Falta documentación',
    tone: 'caution',
    fallbackMessage: 'Subí la documentación de tu depósito para que podamos revisar tu cuenta.',
    uploadCta: 'Subir documentos'
  },
  pending_review: {
    label: 'En revisión',
    tone: 'info',
    fallbackMessage: 'Tu cuenta está en revisión. Te avisamos por mail en cuanto esté aprobada.'
  },
  rejected: {
    label: 'Rechazada',
    tone: 'critical',
    fallbackMessage: 'Tu cuenta fue rechazada. Revisá los motivos y volvé a enviar la documentación.',
    showsReason: true,
    uploadCta: 'Volver a subir documentos'
  },
  suspended: {
    label: 'Suspendida',
    tone: 'critical',
    fallbackMessage: 'Tu cuenta está suspendida. Contactate con Nexvia para más información.',
    showsReason: true
  }
};

const MONTEVIDEO_DATE = new Intl.DateTimeFormat('es-UY', {
  timeZone: 'America/Montevideo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric'
});

/**
 * NEX-1164 — `submittedAt`/`reviewedAt` are TIMESTAMPTZ instants serialised as UTC ("…Z"), so the
 * day shown is the one in Montevideo: cutting the string at the "T" printed the UTC day, one day
 * ahead for anything stamped after 21:00 in Uruguay. A bare `YYYY-MM-DD` (no time of day) keeps
 * its own calendar day, never routed through a `Date`.
 */
function formatApprovalDate(value: string): string {
  if (!value.includes('T')) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value.split('T')[0] : MONTEVIDEO_DATE.format(date);
}

/**
 * NEX-1164 design pass (spec 2026-10-18 §3.6) — the mock's "Alta aprobada el 2 de agosto de 2026".
 * Same day rule as `formatApprovalDate`: an instant is read in Montevideo, a bare `YYYY-MM-DD` keeps
 * its own calendar day (formatted in UTC, where its midnight lives). es-UY spells the ninth month
 * "setiembre".
 */
const LONG_DATE = { day: 'numeric', month: 'long', year: 'numeric' } as const;
const MONTEVIDEO_LONG_DATE = new Intl.DateTimeFormat('es-UY', { ...LONG_DATE, timeZone: 'America/Montevideo' });
const CALENDAR_LONG_DATE = new Intl.DateTimeFormat('es-UY', { ...LONG_DATE, timeZone: 'UTC' });

function formatApprovalLongDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatApprovalDate(value);
  return (value.includes('T') ? MONTEVIDEO_LONG_DATE : CALENDAR_LONG_DATE).format(date);
}

/**
 * NEX-1151: this used to be a hand-rolled `role="status"` div per branch, with its own
 * hardcoded Tailwind tone classes (amber/sky/red/blue). Now the real `Alert` molecule (read
 * `Alert.d.ts`/`Alert.js` before this swap) for every branch, including `verified`.
 *
 * `verified` maps to `tone="info"` (solid light-blue card, blue icon, blue text throughout) —
 * NOT `tone="positive"` (green) despite "aprobada" suggesting green at first glance: a Claude
 * Design mockup screenshot of this exact banner showed blue chrome, confirmed directly against
 * that reference rather than assumed from the word alone.
 *
 * No icon is passed manually: `Alert` already renders a tone-appropriate icon of its own
 * (`Info`/`CheckCircle`/`Warning`/`WarningCircle`, see `Alert.js`) before its title/children, so
 * the old manual `SealCheckIcon` for `verified` is dropped — the tone-info banner now shows
 * `Alert`'s own `Info` icon instead, not a seal-check.
 *
 * One real behavior change, not a cosmetic one, falls directly out of adopting the real
 * component rather than being chosen here: `Alert` itself renders `role="alert"` for
 * `tone="critical"` and `role="status"` for every other tone (hardcoded in `Alert.js`, not a
 * prop). `rejected`/`suspended` are `critical`, so those two banners are now `role="alert"`
 * instead of `role="status"` — arguably the more correct semantic for a banner announcing an
 * account that cannot currently operate. See this component's test file for the one assertion
 * updated to match.
 *
 * NEX-1164 design pass (2026-10-18, spec §3.6): `verified` takes the mock's long date and copy.
 * The mock's `SealCheck` glyph is not drawn: `Alert` (1.26.0) has no icon prop and paints its own
 * `Info` for the info tone. The other four states keep their copy and tone.
 */
export function ApprovalStatusBanner({ status, nextStep, reviewedAt, rejectionReason, submittedAt }: ApprovalStatusBannerProps) {
  if (status === 'verified') {
    return (
      <Alert tone="info" title={reviewedAt ? `Alta aprobada el ${formatApprovalLongDate(reviewedAt)}` : 'Alta aprobada'}>
        Podés cotizar solicitudes. Facturás con IVA por el servicio de guardado que prestás.
      </Alert>
    );
  }

  const copy = STATUS_COPY[status];
  const message = nextStep?.message ?? copy.fallbackMessage;
  const reason = copy.showsReason ? rejectionReason?.trim() : undefined;
  const submittedOn = status === 'pending_review' && submittedAt ? formatApprovalDate(submittedAt) : null;

  return (
    <Alert tone={copy.tone} title={copy.label}>
      <div className="flex flex-col gap-2">
        <p className="m-0">{message}</p>
        {submittedOn && <p className="m-0">Recibimos tu documentación el {submittedOn}.</p>}
        {reason && <p className="m-0">Motivo: {reason}</p>}
        {copy.uploadCta && (
          <div>
            <Button as="a" href={`#${DOCUMENTS_SECTION_ID}`} variant="outline" size="xs" showArrow={false}>
              {copy.uploadCta}
            </Button>
          </div>
        )}
      </div>
    </Alert>
  );
}
